import { FC } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import $api from "../../shared/api/axios";
import { AppLoader } from "../../shared/components/AppLoader";
import { getRoutePromptsFeature } from "../../shared/const/router";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { TActiveRevision, formatRevisionLabel } from "./revision";

type TFeature = {
  featureKey: string;
  label: string;
  promptCount: number;
  activeRevision: TActiveRevision;
  supportsTests: boolean;
};

export const FeaturesListPage: FC = () => {
  const { data: features = [], isLoading } = useQuery<TFeature[]>({
    queryKey: ["admin-prompt-features"],
    queryFn: async () => {
      const response = await $api.get("/admin/prompts/features");
      return response?.data || [];
    }
  });

  const renderContent = () => {
    if (isLoading) return <AppLoader />;
    if (features.length === 0) {
      return <div className="p-8 text-center text-gray-500">No features registered.</div>;
    }
    return (
      <div className="rounded-lg border bg-card shadow-xs overflow-hidden divide-y">
        {features.map((feature) => (
          <Link
            key={feature.featureKey}
            to={getRoutePromptsFeature(feature.featureKey)}
            className="flex items-center justify-between px-6 py-4 hover:bg-gray-50"
          >
            <div>
              <div className="text-[14px] font-medium text-gray-900">{feature.label}</div>
              <FigmaTypography component="div" variant="body-3" className="text-gray-400">{feature.featureKey}</FigmaTypography>
            </div>
            <div className="text-right">
              <FigmaTypography component="div" variant="body-3" className="text-gray-500">
                {feature.promptCount} {feature.promptCount === 1 ? "prompt" : "prompts"}
              </FigmaTypography>
              <FigmaTypography component="div" variant="body-3" className="text-gray-400">
                {formatRevisionLabel(feature.activeRevision)}
              </FigmaTypography>
            </div>
          </Link>
        ))}
      </div>
    );
  };

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <FigmaTypography component="h1" variant="heading-1" className="mb-6">Prompts</FigmaTypography>
      <p className="text-[14px] text-gray-500 mb-6">
        The prompts AI features run with. Text comes from the feature&apos;s active revision; anything a revision does not
        carry falls back to the default defined in code.
      </p>

      {renderContent()}
    </div>
  );
};
